<template lang="pug">
.program-of-study-container
  CteTopBar.top-nav(:title="'Program of Study'" :subtitle="topBarSubtitle")
  ProgramOfStudyModal.program-select-modal(
    :isOpen="programSelectModalOpen"
    :sector="activeSector"
    :pathways="activeSectorPathways"
    @chooseProgram="tryChooseProgram($event)"
    @close="activeSector = null"
  )

  EditSectorModal.program-select-modal(
    :isOpen="programEditModalOpen"
    :sector="editSector"
    :pathways="editSectorPathways"
    @remove-school="removeSchool"
    @close="editSector = null"
  )

  AlertModal.program-alert-modal(
    :isOpen="programAlertModalOpen"
    :message="'Changing your Program of Study will remove the following courses from your plan. You can still find these courses in the All Saved Courses tab.'"
    :items="coursesToBeRemovedNames"
    @cancel="cancelNewProgram"
    @accept="acceptNewProgram"
  )
  .body
    .loading-circles(v-if="loadingChosenProgram || industrySectorsLoading")
      span
      span
      span
    //- If chosenProgramObj is available, then the user has selected a program
    .chosen-program-container(v-else-if="chosenProgramObj && !showViewAllPrograms")
      .top-buttons
        .view-all-button(@click="onViewAllProgramsClick") View all Programs of Study
        DownloadLink.download-button(@click="downloadProgram")
      CtePathwayPreview.preview(:ctePathway="chosenProgramObj")
    .view-all-programs-container(v-else)
      p.view-programs-directions Select a CTE Pathway to view those Program of Study option(s):
      p.no-sectors.spacey(v-if="!sectors.length") No sectors for {{ setSchool }}
      .cte-pathways-container(v-else)
        CteSectorCard(
          v-for="(sector, i) in sectors"
          :key="i"
          :sector="sector"
          @click="onSectorClick(sector)"
          @editClick="onEditClick(sector)"
        )
</template>

<script>
import ProgramOfStudyModal from '@/components/ProgramOfStudyModal';
import AlertModal from '@/components/AlertModal';
import EditSectorModal from '@/components/EditSectorModal';
import CteTopBar from '@/components/CteTopBar';
import CtePathwayPreview from '@/components/CtePathwayPreview';
import UndoSnackbar from '@/components/UndoSnackbar';
import CteSectorCard from '@/components/CteSectorCard';
import DownloadLink from '@/components/DownloadLink';
import downloadCTEPathway from '@/lib/downloadCTEPathway';

export default {
  name       : 'program-of-study',
  components : {
    ProgramOfStudyModal,
    AlertModal,
    CteTopBar,
    CtePathwayPreview,
    UndoSnackbar,
    CteSectorCard,
    DownloadLink,
    EditSectorModal
  },

  data() {
    return {
      showViewAllPrograms  : false,
      showSnackbar         : false,
      activeSector         : null,
      activeSectorPathways : null,
      editSector           : null,
      editSectorPathways   : null,

      // The program that the user is trying to choose
      tentativeProgram : null,

      // Courses to be displayed in the Alert Modal if the tentative program
      // does not include any planned CTE courses for the currently chosen program.
      // Very likely to be nonzero unless the student hasn't planned any CTE courses
      // when trying to change programs.
      coursesToBeRemoved : [],
    };
  },

  computed : {
    industrySectorsLoading() {
      return this.$store.getters.industrySectorsLoading;
    },

    chosenProgram() {
      return this.$store.getters.chosenProgram;
    },

    chosenProgramObj() {
      return this.$store.getters.getCTEPathwayByID( this.chosenProgram );
    },

    loadingChosenProgram() {
      return this.$store.getters.loadingChosenProgram;
    },

    topBarSubtitle() {
      if ( this.showViewAllPrograms ) {
        return null;
      }
      return this.chosenProgramObj ? this.chosenProgramObj.name : '';
    },

    setSchool() {
      return this.$store.getters.setSchool;
    },

    // Returns only the industry sectors provided by the setSchool filter
    sectors() {
      return this.$store.getters.filteredIndustrySectors;
    },

    programSelectModalOpen() {
      return !!this.activeSector;
    },

    programEditModalOpen() {
      return !!this.editSector;
    },

    programAlertModalOpen() {
      return !!this.tentativeProgram;
    },

    coursesToBeRemovedNames() {
      return this.coursesToBeRemoved.map( course => course.name );
    }
  },

  methods : {
    removeSchool( pathwayID, schoolName ) {
      this.editSectorPathways = this.editSectorPathways.map( ( pathway ) => {
        if ( pathway.id === pathwayID ) {
          const schools = Object.keys( pathway.schools );
          schools.forEach( ( school ) => {
            if ( school === schoolName ) {
              pathway.schools[school].provides = false;
            }
          } );
        }
        return pathway;
      } );
    },

    async onSectorClick( sector ) {
      this.activeSector = sector;
      const pathways = this.$store.getters.getCTEPathwaysBySector( this.activeSector.key );
      this.activeSectorPathways = pathways;
    },

    async onEditClick( sector ) {
      this.editSector = sector;
      const pathways = this.$store.getters.getCTEPathwaysBySector( this.editSector.key );
      this.editSectorPathways = pathways;
    },

    onViewAllProgramsClick() {
      this.showViewAllPrograms = true;
    },

    /**
     * @param {object} newProgram CTE Pathway object
     */
    tryChooseProgram( newProgram ) {
      // console.log( 'Choosing new program', newProgram );
      // console.log( 'Current program', this.chosenProgram );
      if ( !this.$store.getters.user ) {
        this.$store.dispatch( 'openUserSwiper' );
        return;
      }

      this.tentativeProgram = newProgram;

      if ( !this.chosenProgram ) {
        this.acceptNewProgram();
        return;
      }

      if ( newProgram.id === this.chosenProgramObj.id ) {
        this.activeSector = null;
        this.showViewAllPrograms = false;
        this.tentativeProgram = null;
        return;
      }

      const oldProgramCourses = this.$store.getters.getCoursesByPathway( this.chosenProgram );
      const currPlannedCourses = this.$store.getters.plannedCourses.filter( pc => oldProgramCourses.find( oc => oc.id === pc.id ) );
      const newProgramCourses = this.$store.getters.getCoursesByPathway( newProgram.id );
      const coursesToBeRemoved = currPlannedCourses.filter( currCourse => !newProgramCourses.find( newCourse => currCourse.id === newCourse.id ) );

      if ( coursesToBeRemoved.length > 0 ) {
        this.coursesToBeRemoved = coursesToBeRemoved;
        this.tentativeProgram = newProgram;
      }
      else {
        this.acceptNewProgram();
      }
    },

    acceptNewProgram() {
      this.$store.dispatch( 'chooseProgram', this.tentativeProgram.id );
      this.tentativeProgram = null;
      this.activeSector = null;
      this.showViewAllPrograms = false;
    },

    cancelNewProgram() {
      this.tentativeProgram = null;
    },

    downloadProgram() {
      downloadCTEPathway( this.chosenProgramObj );
    }
  }
};
</script>

<style lang="scss" scoped>

.program-of-study-container {
    position: relative;
    width: 100%;
    height: 100vh;
    display: flex;
    flex-direction: column;

  .program-select-modal {
    z-index: 2;
  }

  .program-alert-modal {
    z-index: 3;
  }

  .top-nav {
    flex-shrink: 0;
    z-index: 1;
  }

    .body {
        flex-grow: 1;
        overflow: auto;
    padding: 30px;

    .chosen-program-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      height: 100%;
      margin: 0 auto;

      .program-display {
        width: 100%;
        height: 100%;
        border: none;
      }

      .preview {
        width: 100%;
        max-width: 1200px;
        border-radius: 10px;
      }

      .top-buttons {
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;
        // margin-bottom: 30px;
        color: #1C4587;

        .view-all-button {
          font-weight: 500;
          font-size: 16px;
          cursor: pointer;
        }

        .download-button {
          margin-left: 30px;
        }
      }
    }

    .view-all-programs-container {
      .view-programs-directions {
        font-weight: 500;
        font-size: 16px;
        letter-spacing: -0.5px;
      }

      .no-sectors {
        text-align: center;
        margin-top: 50px;
      }

      .cte-pathways-container {
        margin-top: 30px;
        display: grid;
        grid-template-columns: 1fr;
        gap: 20px 20px;

        @include sm {
          grid-template-columns: repeat(2, 1fr);
        }
        @include md {
          grid-template-columns: repeat(3, 1fr);
        }
        @include lg {
          grid-template-columns: repeat(4, 1fr);
        }
        @include xl {
          grid-template-columns: repeat(5, 1fr);
        }
      }
    }

    }
}

.snackbar {
    z-index: 2;
}
</style>
